import type {QueueRecord, RetryQueueOptions, TrackingEventKind} from './types';
import {isBrowser} from './utils';

const DEFAULTS: Required<Omit<RetryQueueOptions, 'storageKey'>> = {
  maxSize: 100,
  maxAgeMs: 7 * 24 * 60 * 60 * 1000,
  maxAttempts: 8,
  baseDelayMs: 1_000,
  maxDelayMs: 5 * 60 * 1000,
  flushIntervalMs: 15_000,
};

export class RetryQueue {
  readonly flushIntervalMs: number;
  private readonly options: Required<RetryQueueOptions>;
  private memory: QueueRecord[] = [];
  private persistentStorageAvailable: boolean | null = null;

  constructor(options: RetryQueueOptions = {}) {
    this.options = {
      storageKey: options.storageKey ?? 'biqli_event_queue_v2',
      maxSize: this.positiveInteger(options.maxSize, DEFAULTS.maxSize),
      maxAgeMs: this.positiveInteger(options.maxAgeMs, DEFAULTS.maxAgeMs),
      maxAttempts: this.positiveInteger(options.maxAttempts, DEFAULTS.maxAttempts),
      baseDelayMs: this.positiveInteger(options.baseDelayMs, DEFAULTS.baseDelayMs),
      maxDelayMs: this.positiveInteger(options.maxDelayMs, DEFAULTS.maxDelayMs),
      flushIntervalMs: this.positiveInteger(options.flushIntervalMs, DEFAULTS.flushIntervalMs),
    };
    this.flushIntervalMs = this.options.flushIntervalMs;
    this.save(this.load());
  }

  enqueue(kind: TrackingEventKind, id: string, payload: Record<string, unknown>): void {
    const queue = this.load().filter(record => record.id !== id || record.kind !== kind);
    queue.push({
      version: 1,
      id,
      kind,
      payload,
      createdAt: Date.now(),
      attempts: 0,
      nextAttemptAt: Date.now(),
    });
    this.save(queue.slice(-this.options.maxSize));
  }

  due(now = Date.now()): QueueRecord | null {
    return this.load()
      .filter(record => record.nextAttemptAt <= now)
      .sort((left, right) => left.nextAttemptAt - right.nextAttemptAt)[0] ?? null;
  }

  remove(kind: TrackingEventKind, id: string): void {
    this.save(this.load().filter(record => record.id !== id || record.kind !== kind));
  }

  retry(kind: TrackingEventKind, id: string): void {
    const queue = this.load();
    const record = queue.find(candidate => candidate.id === id && candidate.kind === kind);
    if (!record) return;
    record.attempts += 1;
    if (record.attempts >= this.options.maxAttempts) {
      this.remove(kind, id);
      return;
    }
    const exponential = Math.min(
      this.options.maxDelayMs,
      this.options.baseDelayMs * 2 ** Math.max(0, record.attempts - 1),
    );
    const jitter = exponential * (Math.random() * 0.4 - 0.2);
    record.nextAttemptAt = Date.now() + Math.max(250, Math.round(exponential + jitter));
    this.save(queue);
  }

  clear(): void {
    this.save([]);
  }

  private load(): QueueRecord[] {
    let candidate: unknown = this.memory;
    if (isBrowser() && this.persistentStorageAvailable !== false) {
      try {
        const raw = window.localStorage.getItem(this.options.storageKey);
        candidate = raw ? JSON.parse(raw) : [];
        this.persistentStorageAvailable = true;
      } catch {
        this.persistentStorageAvailable = false;
        candidate = this.memory;
      }
    }
    if (!Array.isArray(candidate)) return [];
    const cutoff = Date.now() - this.options.maxAgeMs;
    return candidate.filter((value): value is QueueRecord =>
      this.isRecord(value) && value.createdAt >= cutoff,
    );
  }

  private save(queue: QueueRecord[]): void {
    this.memory = queue;
    if (!isBrowser()) return;
    try {
      if (queue.length) window.localStorage.setItem(this.options.storageKey, JSON.stringify(queue));
      else window.localStorage.removeItem(this.options.storageKey);
      this.persistentStorageAvailable = true;
    } catch {
      this.persistentStorageAvailable = false;
      // Keep an in-memory queue when persistent storage is unavailable.
    }
  }

  private isRecord(value: unknown): value is QueueRecord {
    if (!value || typeof value !== 'object') return false;
    const record = value as Partial<QueueRecord>;
    return record.version === 1 &&
      typeof record.id === 'string' &&
      ['click', 'lead', 'sale'].includes(record.kind ?? '') &&
      !!record.payload && typeof record.payload === 'object' && !Array.isArray(record.payload) &&
      typeof record.createdAt === 'number' &&
      typeof record.attempts === 'number' &&
      typeof record.nextAttemptAt === 'number';
  }

  private positiveInteger(value: number | undefined, fallback: number): number {
    return Number.isInteger(value) && (value as number) > 0 ? value as number : fallback;
  }
}
